import React, { useEffect, useState } from 'react';

function ProjectDetails({ project, onClose }) {
    const [lightboxIndex, setLightboxIndex] = useState(null);

    const isLightboxOpen = lightboxIndex !== null;
    const screenshots = project?.demoScreenshots || [];

    // Keyboard navigation (Escape to close, Left/Right arrows to slide)
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                if (isLightboxOpen) {
                    setLightboxIndex(null);
                } else {
                    onClose();
                }
            } else if (isLightboxOpen) {
                if (event.key === 'ArrowRight') {
                    handleNext();
                } else if (event.key === 'ArrowLeft') {
                    handlePrev();
                }
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLightboxOpen, lightboxIndex, screenshots.length, onClose]);

    if (!project) return null;

    const handlePrev = (e) => {
        if (e) e.stopPropagation();
        setLightboxIndex((prev) => (prev === 0 ? screenshots.length - 1 : prev - 1));
    };

    const handleNext = (e) => {
        if (e) e.stopPropagation();
        setLightboxIndex((prev) => (prev === screenshots.length - 1 ? 0 : prev + 1));
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="project-modal glass-modal" onClick={(e) => e.stopPropagation()}>
                {/* Modal Header */}
                <div className="modal-header d-flex align-items-center justify-content-between">
                    <div>
                        <span className="project-badge">{project.category || 'Mobile & Web'}</span>
                        <h2 className="modal-title mt-2">{project.projectTitle}</h2>
                        <p className="modal-tech-muted mb-0">{project.techUsed}</p>
                    </div>
                    <button type="button" className="btn-close-modal" onClick={onClose} aria-label="Close">
                        &times;
                    </button>
                </div>

                {/* Modal Main Body */}
                <div className="modal-body">
                    <div className="detail-grid">
                        <div className="detail-block">
                            <span className="detail-label">Key Features</span>
                            <ul className="features-list">
                                {project.keyFeatures.split('\n').map((feature, index) => (
                                    <li key={index}>{feature.replace(/^•\s*/, '')}</li>
                                ))}
                            </ul>
                        </div>
                        <div className="detail-block">
                            <span className="detail-label">Project Overview</span>
                            <p className="detail-description">{project.description}</p>
                        </div>
                    </div>

                    {/* Scrollable Screenshots Gallery Section */}
                    {screenshots.length > 0 && (
                        <div className="modal-gallery-section">
                            <span className="detail-label">Project Screenshots & Visuals</span>
                            <p className="gallery-hint">Click any screenshot to expand full view</p>

                            <div className="gallery-scroll-container">
                                {screenshots.map((imgSrc, idx) => (
                                    <div
                                        key={idx}
                                        className="gallery-card"
                                        onClick={() => setLightboxIndex(idx)}
                                    >
                                        <img
                                            src={imgSrc}
                                            alt={`${project.projectTitle} screenshot ${idx + 1}`}
                                            className="gallery-thumbnail"
                                        />
                                        <div className="gallery-card-overlay">
                                            <span>🔍 View</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Modal Footer */}
                <div className="modal-footer d-flex justify-content-between align-items-center">
                    <span className="footer-tagline">Built with performance & precision</span>
                    <a href={project.link} target="_blank" rel="noreferrer" className="btn btn-primary project-code-btn">
                        View Source Code
                    </a>
                </div>
            </div>

            {/* Lightbox / Fullscreen Slideshow Overlay */}
            {isLightboxOpen && (
                <div className="lightbox-overlay" onClick={() => setLightboxIndex(null)}>
                    <button
                        className="lightbox-close-btn"
                        onClick={() => setLightboxIndex(null)}
                    >
                        &times;
                    </button>

                    <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
                        {screenshots.length > 1 && (
                            <button className="lightbox-arrow left" onClick={handlePrev}>
                                &#10094;
                            </button>
                        )}

                        <div className="lightbox-image-wrapper">
                            <img
                                src={screenshots[lightboxIndex]}
                                alt={`Expanded view ${lightboxIndex + 1}`}
                                className="lightbox-image"
                            />
                        </div>

                        {screenshots.length > 1 && (
                            <button className="lightbox-arrow right" onClick={handleNext}>
                                &#10095;
                            </button>
                        )}
                    </div>

                    <div className="lightbox-counter">
                        {lightboxIndex + 1} / {screenshots.length}
                    </div>
                </div>
            )}
        </div>
    );
}

export default ProjectDetails;